/**
 * Shopify → QuickBooks landing copy (SMB, non-technical).
 * Avoid: data, pipeline, integration platform.
 */

export const SHOPIFY_QB_LANDING = {
    path: '/shopify-to-quickbooks',
    title: 'Shopify to QuickBooks | No Exports, No Retyping | Data Connector',
    description:
        'Stop the weekly export grind. Shopify orders and sales stay in QuickBooks—no retyping, no fixing mismatches. Start free. No credit card. About five minutes.',

    hero: {
        eyebrow: 'For Shopify stores on QuickBooks',
        headline: 'Still exporting orders—then retyping them in QuickBooks?',
        lines: [
            'Export. Copy. Paste. Fix totals. Repeat. Every week.',
            'We don’t replace your accounting—we keep it up to date.',
            'No exports for that step. No retyping rows. QuickBooks matches Shopify on the schedule you pick.',
        ],
        cta: 'Start free',
        ctaNote: 'No credit card. Takes about 5 minutes.',
    },

    visual: {
        heading: 'Shopify → QuickBooks',
        sub: 'Orders and sales show up in QuickBooks. You drop the weekly CSV habit.',
    },

    problem: {
        kicker: 'Sound familiar?',
        heading: 'The work that eats your week',
        bullets: [
            'Friday night: export orders from Shopify into another CSV file.',
            'Monday morning: paste into QuickBooks—and chase numbers that do not match.',
            'Hours lost fixing tax lines, fees, and order totals by hand.',
            'Next week: same grind. Same spreadsheet. Same stress.',
        ],
    },

    solution: {
        kicker: 'Here’s what changes',
        heading: 'What changes with Data Connector',
        lead:
            'Once connected, sales and orders land in QuickBooks on your schedule—without exports or paste sessions.',
        body: [
            'You keep QuickBooks. You keep Shopify.',
            'No exports for that step. No retyping. No hunting mismatches between Shopify and QuickBooks.',
            'Sales and orders flow in on a rhythm you choose—daily, hourly, or weekly.',
        ],
    },

    /** Mid-page CTAs (match section intent; primary = register) */
    afterSolutionCta: 'Connect Shopify and QuickBooks in minutes',
    afterBeforeAfterCta: 'Start free — stop the weekly export cycle',

    whatUpdates: {
        kicker: 'Inside QuickBooks',
        heading: 'What shows up in QuickBooks',
        intro: 'You stay in charge of your books. Typical updates include:',
        items: [
            'Every order from Shopify—no missing details',
            'Totals that roll up to what you actually sold',
            'Tax amounts your reports need',
            'Fees included—so your totals actually match payouts',
        ],
        note: 'What you get depends on how you run the store—we match what you already track in QuickBooks.',
    },

    howItWorks: {
        heading: 'How it works',
        subline: 'Three steps. Same apps you already use.',
        steps: [
            { title: 'Connect both apps', text: 'Sign in to Shopify and QuickBooks. We walk you through it.' },
            { title: 'Pick what moves', text: 'Choose orders, sales, and timing—daily, hourly, or weekly.' },
            {
                title: 'Let it run',
                text: 'QuickBooks stays up to date for you. No export menu. No Sunday-night paste session.',
            },
        ],
    },

    beforeAfter: {
        heading: 'Before vs after',
        subline: 'Same workweek—two different outcomes.',
        beforeLabel: 'Before',
        afterLabel: 'After',
        beforeItems: [
            'Export orders every week from Shopify',
            'Retype or paste rows into QuickBooks',
            'Fix totals, taxes, and fees that never line up',
            'Rebuild the same report—and still doubt the numbers',
        ],
        afterItems: [
            'No export for that step',
            'No retyping into QuickBooks',
            'No fixing mismatched totals by hand',
            'Same QuickBooks reports—numbers that match Shopify',
        ],
    },

    benefits: {
        kicker: 'What you get',
        heading: 'Why store owners switch',
        subline: 'Less busywork around QuickBooks and Shopify—more time for the shop.',
        items: [
            'Hours back every week—time you used to spend on exports and paste.',
            'Less manual work: no retyping orders, no second pass through fees and taxes.',
            'Fewer QuickBooks surprises when Shopify already told you the truth.',
            'Plain setup. No code. No IT project.',
        ],
    },

    pricing: {
        heading: 'Simple two-part pricing',
        lead: '$15 per app you connect each month, plus $2 for every million rows synced.',
        cards: [
            {
                kicker: 'Live sync',
                amount: '$15',
                line: '/ month per app',
                highlight: 'Shopify + QuickBooks\n= $30/month',
                icon: 'sync',
            },
            {
                kicker: 'Rows synced',
                amount: '$2',
                line: 'per 1 million rows synced',
                detail:
                    'We count rows as they sync. Up to 1 million rows in a month costs $2.\nEach additional million adds another $2.',
                icon: 'sheet',
            },
        ],
        summary: {
            headline: 'Most Shopify + QuickBooks stores pay $32–$50/month',
            lines: [
                '$30/month for the two apps: $15 for Shopify and $15 for QuickBooks.',
                'About $2/month for rows at typical order volume (under a million rows synced).',
                'No long-term contract—cancel anytime.',
            ],
            supporting: 'Higher order volume means more rows synced; each extra million rows adds $2 to your bill.',
        },
        primaryCta: 'Start free — takes 5 minutes',
        ctaSecondary: 'See full pricing',
        ctaTo: '/pricing',
    },

    trustAssurances: {
        heading: 'No surprises in your books—or your bill',
        items: [
            'Your numbers match between Shopify and QuickBooks',
            'You stay in control—nothing changes without you',
            'Pricing is predictable—no hidden fees',
            'Set it up in minutes, not days',
        ],
    },

    finalCta: {
        heading: 'Done losing nights to exports and retyping?',
        sub: 'Start free. No credit card. About five minutes.',
        button: 'Start free — your books stay in sync starting today',
    },

    secondaryCta: {
        label: 'See how it works',
        to: '/shopify-to-quickbooks#how-shopify-qb-works',
    },
};
